import { useEffect, useRef, useState } from "react";
import "@/index.css";
import "@/presentation.css";

import Header from "./components/Header";
import ProgressRail from "./components/ProgressRail";
import S01Cover from "./components/sections/S01Cover";
import S02Gdansk from "./components/sections/S02Gdansk";
import S03UAE from "./components/sections/S03UAE";
import S05Wetra from "./components/sections/S05Wetra";
import S06Ultima88 from "./components/sections/S06Ultima88";
import S07Brabus55 from "./components/sections/S07Brabus55";
import S08Ultima44 from "./components/sections/S08Ultima44";
import S09QA from "./components/sections/S09QA";
import S12PressKits from "./components/sections/S12PressKits";
import S13Thanks from "./components/sections/S13Thanks";

/* ─────────────────────────────────────────────────────────────
   SUNREEF · Cannes Yachting Festival Press Conference
   13 scroll-tell slides · native motion only
   ───────────────────────────────────────────────────────────── */

const SLIDES = [
  { id: "cover", label: "cannes 2026", Comp: S01Cover },
  { id: "gdansk", label: "gdańsk expansion", Comp: S02Gdansk },
  { id: "uae", label: "uae expansion", Comp: S03UAE },
  { id: "wetra", label: "wetra", Comp: S05Wetra },
  { id: "ultima-88", label: "ultima 88", Comp: S06Ultima88 },
  { id: "brabus-55", label: "brabus ultima 55", Comp: S07Brabus55 },
  { id: "ultima-44", label: "ultima 44", Comp: S08Ultima44 },
  { id: "qa", label: "q&a", Comp: S09QA },
  { id: "press-kits", label: "press kits", Comp: S12PressKits },
  { id: "thanks", label: "life in full", Comp: S13Thanks },
];

function App() {
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const barRef = useRef(null);

  useEffect(() => {
    document.title = "Sunreef · Cannes Yachting Festival — Press Conference 2026";
  }, []);

  /* Active slide — section crossing viewport centre */
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const idx = SLIDES.findIndex((s) => s.id === e.target.id);
          if (idx >= 0) {
            activeRef.current = idx;
            setActive(idx);
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );
    SLIDES.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  /* Top progress bar */
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        const p = total > 0 ? window.scrollY / total : 0;
        if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
        if (p > 0.985 && activeRef.current !== SLIDES.length - 1) {
          activeRef.current = SLIDES.length - 1;
          setActive(SLIDES.length - 1);
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* Keyboard navigation — presentation style */
  const goTo = (idx) => {
    const clamped = Math.min(Math.max(idx, 0), SLIDES.length - 1);
    const el = document.getElementById(SLIDES[clamped].id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "PageDown") {
        e.preventDefault();
        goTo(activeRef.current + 1);
      } else if (e.key === "ArrowUp" || e.key === "PageUp") {
        e.preventDefault();
        goTo(activeRef.current - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        goTo(0);
      } else if (e.key === "End") {
        e.preventDefault();
        goTo(SLIDES.length - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Slide fade-on-scroll — each slide melts into the next */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.target.setAttribute("data-fade", e.isIntersecting ? "in" : "out"));
      },
      { threshold: 0.15 }
    );
    SLIDES.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  /* Subtle parallax on immersive media */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf = 0;
    const tick = () => {
      raf = 0;
      const vh = window.innerHeight;
      document.querySelectorAll(".about-immersive-media video, .about-immersive-media img").forEach((el) => {
        const parent = el.closest(".about-immersive-media");
        if (!parent) return;
        const r = parent.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const mid = r.top + r.height / 2;
        el.style.transform = `translate3d(0, ${(mid - vh / 2) * -0.05}px, 0)`;
      });
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    tick();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <div className="progress-bar" ref={barRef} data-testid="progress-bar" />
      <Header active={active} total={SLIDES.length} />
      <ProgressRail slides={SLIDES} active={active} onGo={goTo} />
      <main>
        {SLIDES.map(({ id, Comp }) => (
          <Comp key={id} />
        ))}
      </main>
    </div>
  );
}

export default App;
